import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CategoryList from 'components/CategoryList';

// Extract unique macro categories (ignoring micros) from either an array of
// { macroCategory } items or an object of the shape { macros: [...] }.
const getMacroCategories = (input) => {
  const macroIds = [];
  const addMacro = (macro) => {
    if (typeof macro !== 'number' || macroIds.includes(macro)) return;
    macroIds.push(macro);
  };

  if (Array.isArray(input)) {
    input.forEach((x) => addMacro(x?.macroCategory ?? x?.macroCategoryId));
  } else if (input && typeof input === 'object') {
    (Array.isArray(input.macros) ? input.macros : []).forEach(addMacro);
  }

  return macroIds.map((macroCategory) => ({ macroCategory, microCategory: [] }));
};

const PANEL_WIDTH = 264;

/**
 * Renders a single, non-wrapping row of macro category icons. When there are
 * more than `maxVisible` categories it shows the first `maxVisible` and a
 * "+X" button; clicking the button opens a floating panel (rendered in a
 * portal so it is never clipped by the card) listing the remaining categories.
 */
const CategoryOverflow = ({
  categories,
  maxVisible = 7,
  macroIconClass = 'w-6 h-6',
  align = 'end',
  visibleContainerClass,
  panelContainerClass,
  buttonClass,
}) => {
  const { t } = useTranslation();
  const macros = getMacroCategories(categories);
  const count = macros.length;

  const hasOverflow = count > maxVisible;
  const visible = hasOverflow ? macros.slice(0, maxVisible) : macros;
  const hidden = hasOverflow ? macros.slice(maxVisible) : [];

  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen((o) => !o);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = () => close();
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!count) return null;

  const rowClass =
    `flex flex-row flex-nowrap items-center gap-2 overflow-visible ` +
    (align === 'end' ? 'justify-end' : 'justify-start');

  const panel =
    open && rect && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed z-[9998] rounded-2xl bg-white p-3 shadow-xl ring-1 ring-gray-200"
            style={{
              width: PANEL_WIDTH,
              top: rect.bottom + 8,
              left: Math.min(
                Math.max(rect.right - PANEL_WIDTH, 8),
                window.innerWidth - PANEL_WIDTH - 8
              ),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
              {t('listing.more_categories') || 'More categories'}
            </p>
            <CategoryList
              categories={hidden}
              onlyMacro
              withLabel
              macroIconClass={macroIconClass}
              containerClass={
                panelContainerClass ??
                'flex flex-row flex-wrap items-center gap-2 overflow-visible'
              }
            />
          </div>,
          document.body
        )
      : null;

  return (
    <div className={rowClass}>
      <CategoryList
        categories={visible}
        onlyMacro
        withLabel
        macroIconClass={macroIconClass}
        containerClass={
          visibleContainerClass ??
          'flex flex-row flex-nowrap items-center gap-2 overflow-visible'
        }
      />
      {hasOverflow && (
        <button
          ref={btnRef}
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className={
            buttonClass ??
            'shrink-0 inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold leading-none hover:bg-gray-200 transition'
          }
        >
          +{hidden.length}
        </button>
      )}
      {panel}
    </div>
  );
};

export default CategoryOverflow;
